import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import SearchBar from "../../GlobalManagement/Pages/SearchBar";
import toast from "react-hot-toast";
import BackButton from "../../GlobalManagement/Pages/BackButton";
import MobileHeaderToggle from "../../GlobalManagement/Pages/MobileHeaderToggle";
import { FaEye, FaPlusCircle, FaPrint } from "react-icons/fa";
import { Building2, ArrowLeft } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import EyeLogo from "../../assets/dorikaLogo.jpg";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterInput, 
  masterSelect,
  masterTableDatastatus,
  masterTableCellstatus
} from '../../GlobalManagement/Pages/masterPageStyles';

const normalizeEmployeeId = (value) => String(value || "").trim().toUpperCase();
const isExEmployeeId = (value) => normalizeEmployeeId(value).startsWith("EX-");
const getEmployeePrefix = (value) => {
  const normalized = normalizeEmployeeId(value);
  if (!normalized || isExEmployeeId(normalized)) return "";
  return normalized.split("-")[0] || "";
};

const PaySlipGenerateEmployeeList = () => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  const [earningDetails, setEarningDetails] = useState([]);
  const [deductionDetails, setDeductionDetails] = useState([]);

  const [grossSalary, setGrossSalary] = useState(0);
  const [totalDeduction, setTotalDeduction] = useState(0);
  const [netSalary, setNetSalary] = useState(0);
  const [lopAmount, setLopAmount] = useState(0);
  const [inHandSalary, setInHandSalary] = useState(0);

  const [totalWorkingDays, setTotalWorkingDays] = useState(0);
  const [LOP, setLOP] = useState(0);
  const [leaves, setLeaves] = useState(0);
  const [otHours, setOtHours] = useState(0);
  const [otAmount, setOtAmount] = useState(0);
  const [paidDaysSalary, setPaidDaysSalary] = useState(0);
  const [totalPaidDays, setTotalPaidDays] = useState(0); 
  const [employees, setEmployees] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return localStorage.getItem("selectedMonth") || "";
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    return localStorage.getItem("selectedYear") || "";
  });
  const [hasPayslipForMonth, setHasPayslipForMonth] = useState(false);

  // Pagination states matching DepartmentMaster standard
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployeePrefix, setSelectedEmployeePrefix] = useState("ALL");
  const navigate = useNavigate();

  const normalizeSearchInput = (value) => {
    let v = String(value || "").toUpperCase();
    if (/^[A-Z]+\d/.test(v)) {
      v = v.replace(/^([A-Z]+)-?(\d.*)$/, "$1-$2");
    }
    return v;
  };

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      if (isExEmployeeId(emp.employeeID)) return false;
      const prefixMatch =
        selectedEmployeePrefix === "ALL" ||
        getEmployeePrefix(emp.employeeID) === selectedEmployeePrefix;
      const q = String(searchTerm || "").toUpperCase().trim();
      if (!prefixMatch) return false;
      if (!q) return true;
      const name = `${emp.salutation || ""} ${emp.firstName || ""} ${emp.lastName || ""}`.toUpperCase();
      const employeeId = String(emp.employeeID || "").toUpperCase();
      const employeeUserId = String(emp.employeeUserId || "").toUpperCase();
      return name.includes(q) || employeeId.includes(q) || employeeUserId.includes(q);
    });
  }, [employees, selectedEmployeePrefix, searchTerm]);

  const paginatedEmployees = useMemo(() => {
    if (pageSize === -1) return filteredEmployees;
    const start = (currentPage - 1) * pageSize;
    return filteredEmployees.slice(start, start + pageSize);
  }, [filteredEmployees, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize, selectedEmployeePrefix]);

  const employeePrefixOptions = ["ALL", ...new Set(
    employees.map((emp) => getEmployeePrefix(emp.employeeID)).filter(Boolean)
  )];

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedEmployees(filteredEmployees.map(emp => emp._id));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleSelectEmployee = (e, empId) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedEmployees(prev => [...prev, empId]);
    } else {
      setSelectedEmployees(prev => prev.filter(id => id !== empId));
    }
  };

  const handlePrintAllOnePDF = async () => {
    const [tYear, tMonthNum] = selectedMonth.split("-");
    const tMonthName = monthNames[Number(tMonthNum) - 1];
    
    if (selectedEmployees.length === 0) {
      toast.error("Please select at least one employee", { id: "print-select-required" });
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");
    const loadingToast = toast.loading("Generating combined PDF... Please wait.");

    const selectedEmpRows = selectedEmployees
      .map((empId) => employees.find((e) => e._id === empId))
      .filter(Boolean);

    const uniqueDepts = [...new Set(selectedEmpRows.map((emp) => emp.departmentName || "All"))];
    const batchCache = {};

    await Promise.all(
      uniqueDepts.map(async (dept) => {
        try {
          const res = await axios.get(
            `/api/payslips/check-batch?month=${tMonthName}&year=${tYear}&filterDept=${dept}`
          );
          batchCache[dept] = res.data.exists ? res.data.data : null;
        } catch {
          batchCache[dept] = null;
        }
      })
    );

    for (let i = 0; i < selectedEmpRows.length; i++) {
      const emp = selectedEmpRows[i];
      if (!emp) continue;

      const payslip = await fetchLatestPayslip(emp, batchCache[emp.departmentName || "All"]);
      if (!payslip) continue;

      await new Promise(resolve => setTimeout(resolve, 120));
      
      const el = document.getElementById("print-section");
      if (!el) continue;

      const silenceContainer = document.createElement("div");
      silenceContainer.style.position = "fixed";
      silenceContainer.style.top = "0";
      silenceContainer.style.left = "0";
      silenceContainer.style.width = "100vw";
      silenceContainer.style.height = "0";
      silenceContainer.style.overflow = "hidden";
      silenceContainer.style.zIndex = "-9999";
      document.body.appendChild(silenceContainer);

      const clone = el.cloneNode(true);
      clone.style.display = "block";
      clone.style.position = "relative";
      clone.style.width = "100%";
      clone.style.background = "white";
      silenceContainer.appendChild(clone);

      const canvas = await html2canvas(clone, { 
        scale: 2, 
        useCORS: true, 
        allowTaint: true,
        logging: false,
        windowWidth: 1200, 
        windowHeight: clone.scrollHeight
      });

      document.body.removeChild(silenceContainer);

      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      if (i > 0) pdf.addPage();
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
    }

    pdf.save(`All_Payslips_${tMonthName}_${tYear}.pdf`);
    toast.dismiss(loadingToast);
    toast.success("PDF Downloaded");
  };

  const fetchLatestPayslip = async (emp, preloadedBatchHeader = null) => {
    try {
      const [tYear, tMonthNum] = selectedMonth.split("-");
      const tMonthName = monthNames[Number(tMonthNum) - 1];

      let batchHeader = preloadedBatchHeader;
      if (!batchHeader) {
        const res = await axios.get(
          `/api/payslips/check-batch?month=${tMonthName}&year=${tYear}&filterDept=${emp.departmentName || "All"}`
        );
        
        if (!res.data.exists || !res.data.data) {
          toast.error(`No payslip batch found for ${tMonthName} ${tYear}`);
          return null;
        }
        batchHeader = res.data.data;
      }

      const employeeData = batchHeader.employeePayslips.find(
        (s) => s.employeeUserId === emp.employeeUserId
      );

      if (!employeeData) {
        toast.error(`Employee ${emp.firstName} not found in this batch.`);
        return null;
      }

      setSelectedEmployee({ 
        ...emp, 
        employeeName: employeeData.employeeName, 
        employeeUserId: employeeData.employeeUserId,
        employeeID: employeeData.employeeId,
        designationName: employeeData.designationName || emp.designationName,
        doj: employeeData.doj || emp.doj
      });

      setMonth(batchHeader.month); 
      setYear(batchHeader.year);

      setEarningDetails((employeeData.earnings || []).map(e => ({ headName: e.headName, value: e.amount || 0 })));
      setDeductionDetails((employeeData.deductions || []).map(d => ({ headName: d.headName, value: d.amount || 0 })));
      
      setGrossSalary(employeeData.grossSalary || 0);
      setNetSalary(employeeData.netSalary || 0);
      setInHandSalary(employeeData.inHandSalary || employeeData.netSalary || 0); 

      setTotalWorkingDays(employeeData.totalWorkingDays || 0); 
      setLOP(Number(employeeData.lopDays || 0));                     
      setTotalPaidDays(employeeData.totalPaidDays || 0);
      setLeaves(employeeData.leaves || 0);
      setOtHours(employeeData.otHours || 0);
      setOtAmount(employeeData.otAmount || 0);

      return employeeData;
    } catch (err) {
      console.error(err);
      toast.error("Database connection error.");
      return null;
    }
  };

  useEffect(() => {
    const checkMonthPayslip = async () => {
      if (!selectedMonth) {
        setHasPayslipForMonth(false);
        return;
      }

      try {
        const [tYear, tMonthNum] = selectedMonth.split("-");
        const tMonthName = monthNames[Number(tMonthNum) - 1];
        const res = await axios.get(
          `/api/payslips/check-batch?month=${tMonthName}&year=${tYear}&filterDept=All`
        );
        setHasPayslipForMonth(!!res?.data?.exists);
      } catch (err) {
        setHasPayslipForMonth(false);
      }
    };

    checkMonthPayslip();
  }, [selectedMonth]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axios.get("/api/employees");
        setEmployees((res.data || []).filter((emp) => !isExEmployeeId(emp.employeeID)));
      } catch (err) {
        console.error("Fetch Employee Error:", err);
        toast.error("Failed to fetch employee list");
      }
    };
    fetchEmployees();
  }, []);

  const TwoColRow = ({ label1, value1, label2, value2 }) => (
    <div className="flex justify-between mb-1 text-xl">
      {label1 && (
        <div className="flex flex-1">
          <div className="min-w-[170px]">{label1}</div>
          <div className="font-semibold">: {value1 || "N/A"}</div>
        </div>
      )}
      {label2 && (
        <div className="flex flex-1">
          <div className="min-w-[80px] font-semibold">{label2}</div>
          <div className="font-semibold">: {value2 || "N/A"}</div>
        </div>
      )}
    </div>
  );

  const handleDownloadPDF = async () => {
    const el = document.getElementById("print-section");
    if (!el) {
      console.error("print-section not found");
      return;
    }

    const silenceContainer = document.createElement("div");
    silenceContainer.style.position = "fixed";
    silenceContainer.style.top = "0";
    silenceContainer.style.left = "0";
    silenceContainer.style.width = "100vw";
    silenceContainer.style.height = "0";
    silenceContainer.style.overflow = "hidden";
    silenceContainer.style.zIndex = "-9999";
    document.body.appendChild(silenceContainer);

    const clone = el.cloneNode(true);
    clone.style.display = "block";
    clone.style.position = "relative";
    clone.style.width = "100%";
    clone.style.background = "white";
    silenceContainer.appendChild(clone);

    await new Promise((resolve) => setTimeout(resolve, 200));

    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      windowWidth: 1200, 
      windowHeight: clone.scrollHeight
    });

    document.body.removeChild(silenceContainer);

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const margin = 10; 
    const printableWidth = pdfWidth - (margin * 2);
    const pdfHeight = (canvas.height * printableWidth) / canvas.width;
    const xOffset = (pdfWidth - printableWidth) / 2;

    pdf.addImage(imgData, "PNG", xOffset, margin, printableWidth, pdfHeight);

    const fileName = `${selectedEmployee.employeeID}-${selectedEmployee.firstName}_${selectedEmployee.lastName}.pdf`;
    pdf.save(fileName);
  };

  const handlePrintAllSeparateFiles = async () => {
    const [tYear, tMonthNum] = selectedMonth.split("-");
    const tMonthName = monthNames[Number(tMonthNum) - 1];

    if (selectedEmployees.length === 0) {
      toast.error("Please select at least one employee", { id: "print-select-required" });
      return;
    }

    const bulkToast = toast.loading(`Preparing files for ${selectedEmployees.length} employees...`);

    for (let i = 0; i < selectedEmployees.length; i++) {
      const empId = selectedEmployees[i];
      const emp = employees.find((e) => e._id === empId);
      if (!emp) continue;

      const payslip = await fetchLatestPayslip(emp);
      if (!payslip) continue;

      await new Promise((resolve) => setTimeout(resolve, 500));

      const el = document.getElementById("print-section");
      if (!el) continue;

      const silenceContainer = document.createElement("div");
      silenceContainer.style.position = "fixed";
      silenceContainer.style.top = "0";
      silenceContainer.style.left = "0";
      silenceContainer.style.width = "100vw";
      silenceContainer.style.height = "0";
      silenceContainer.style.overflow = "hidden";
      silenceContainer.style.zIndex = "-9999";
      document.body.appendChild(silenceContainer);

      const clone = el.cloneNode(true);
      clone.style.display = "block";
      clone.style.position = "relative";
      clone.style.width = "100%"; 
      clone.style.background = "white";
      silenceContainer.appendChild(clone);

      const canvas = await html2canvas(clone, { 
        scale: 2, 
        useCORS: true, 
        allowTaint: true,
        logging: false,
        windowWidth: 1200, 
        windowHeight: clone.scrollHeight
      });

      document.body.removeChild(silenceContainer);

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const margin = 10; 
      const printableWidth = pdfWidth - (margin * 2);
      const pdfHeight = (canvas.height * printableWidth) / canvas.width;
      const xOffset = (pdfWidth - printableWidth) / 2;

      pdf.addImage(imgData, "PNG", xOffset, margin, printableWidth, pdfHeight);

      const fileName = `Payslip_${emp.employeeID || emp.firstName}_${tMonthName}_${tYear}.pdf`;
      pdf.save(fileName);

      await new Promise((resolve) => setTimeout(resolve, 400));
    }
    
    toast.dismiss(bulkToast);
    toast.success("All individual files downloaded successfully");
  };

  const canPrint = !!selectedMonth && hasPayslipForMonth;

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Restored Header Section using Master Layout Styles */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Building2 size={14} /></div>
              <div><h1 className={masterTitle}>Generate Pay Slip</h1></div>
            </div>
            <div className="flex gap-2">
              <BackButton />
            </div>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full`}>
          <div className={`${masterPanel} flex flex-col overflow-hidden w-full`}>
            
     {/* Top Controls Bar */}
        <div className="border-b border-slate-200 flex-shrink-0 bg-white p-2 sm:p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div className="flex flex-col">
              <label className="font-semibold text-blue-800 text-xs uppercase mb-1 block">Month</label>
              <input
                type="month"
                className="w-full border border-slate-300 py-1 px-3 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={selectedMonth}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedMonth(value);
                  localStorage.setItem("selectedMonth", value);
                  if (value) {
                    const [year, month] = value.split("-");
                    setSelectedYear(year);
                    localStorage.setItem("selectedYear", year);
                  }
                }}
              />
            </div>

            <div className="flex flex-col">
              <label className="font-semibold text-blue-800 text-xs uppercase mb-1 block">Print All</label>
              <button
                type="button"
                className="w-full border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 py-1 px-3 rounded text-sm font-semibold flex items-center justify-center gap-1 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed transition-all shadow-none"
                onClick={handlePrintAllOnePDF}
                disabled={!canPrint}
              >
                <FaPrint size={13} /> Print All
              </button>
            </div>

            <div className="flex flex-col">
              <label className="font-semibold text-blue-800 text-xs uppercase mb-1 block">Print Separate</label>
              <button
                type="button"
                className="w-full border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 py-1 px-3 rounded text-sm font-semibold flex items-center justify-center gap-1 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed transition-all shadow-none"
                onClick={handlePrintAllSeparateFiles}
                disabled={!canPrint}
              >
                <FaPrint size={13} /> Print Separate
              </button>
            </div>

            <div className="flex flex-col">
              <label className="font-semibold text-blue-800 text-xs uppercase mb-1 block">Status</label>
              <select
                value={selectedEmployeePrefix}
                onChange={(e) => setSelectedEmployeePrefix(e.target.value)}
                className="w-full border border-slate-300 py-1 px-3 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {employeePrefixOptions.map((prefix) => (
                  <option key={prefix} value={prefix}>
                    {prefix}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Integrated SearchBar for Pagination Control */}
          <div className="mt-2 pt-2 border-t border-slate-100">
            <SearchBar 
              value={searchTerm} 
              onChange={(val) => {
                setSearchTerm(normalizeSearchInput(val));
                setCurrentPage(1);
              }} 
              placeholder="Search by employee name or code..." 
              totalItems={filteredEmployees.length} 
              currentPage={currentPage} 
              pageSize={pageSize} 
              onPageChange={setCurrentPage} 
              onPageSizeChange={setPageSize} 
            />
          </div>
        </div>

            {/* Table Area */}
            <div className="overflow-x-auto w-full">
              <table className={masterTable}>
                <thead>
                  <tr className={masterStickyHeadRow}>
                    <th className={masterTableData} style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={paginatedEmployees.length > 0 && paginatedEmployees.every((emp) => selectedEmployees.includes(emp._id))}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className={masterTableData} style={{ width: '60px' }}>S.No</th>
                    <th className={masterTableData}>Employee ID</th>
                    <th className={masterTableData}>Employee Name</th>
                    <th className={masterTableData}>Mobile No</th>
                    <th className={masterTableData}>Email</th>
                    <th className={masterTableDatastatus}>Action</th>
                  </tr>
                </thead>
                <tbody className={masterTbody}>
                  {paginatedEmployees && paginatedEmployees.length > 0 ? (
                    paginatedEmployees.map((emp, index) => (
                      <tr key={emp._id} className={masterTableRow}>
                        <td className={masterTableCell}>
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(emp._id)}
                            onChange={(e) => handleSelectEmployee(e, emp._id)}
                          />
                        </td>
                        <td className={masterTableCell}>
                          {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className={masterTableCell}>{emp.employeeID || "-"}</td>
                        <td className={masterTableCell}>
                          {`${emp.salutation || ""} ${emp.firstName || ""} ${emp.lastName || ""}`}
                        </td>
                        <td className={masterTableCell}>{emp.permanentAddress?.mobile || "-"}</td>
                        <td className={masterTableCell}>{emp.permanentAddress?.email || "-"}</td>
                        <td className={masterTableCellstatus}>
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={async () => {
                                if (!canPrint) return;
                                const payslip = await fetchLatestPayslip(emp); 
                                if (payslip) {
                                  handleDownloadPDF(); 
                                }
                              }}
                              disabled={!canPrint}
                              className="text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed p-1 transition-all"
                              title="Print Payslip"
                            >
                              <FaPrint size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                        No employees found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </main>
      </div>

      {/* Hidden Print Section Template */}
      {selectedEmployee && (
        <div
          id="print-section"
          className="hidden print:block border-2 border-black w-[210mm] max-w-full mx-auto py-4 px-12"
          style={{ fontFamily: "sans-serif", fontSize: "14px", minWidth: "210mm" }}
        >
          <div className="flex items-start justify-between px-4">
            <img 
              src={EyeLogo}
              alt="Left Logo"
              className="w-32 h-32 rounded-full object-cover"
            />
            <div className="text-center flex-1 self-start">
              <h1 className="text-3xl font-sans font-bold">DORIKA HOSPITAL</h1>
              <p className="text-base font-normal font-serif tracking-wide">NH 715, Mazgaon, Tezpur- 784154</p>
            </div>
            <img 
              src={EyeLogo}
              alt="Right Logo"
              className="w-32 h-32 rounded-full object-cover"
            />
          </div>

          <div className="text-center mb-4">
            <h2 className="text-2xl font-semibold">PAY SLIP</h2>
          </div>

          <div className="border border-black px-4 py-2 mb-4">
            <div className="mb-4 grid grid-cols-3 gap-4 items-start text-xl">
              <div className="col-span-2 space-y-2">
                <TwoColRow label1="Employee Name" value1={`${selectedEmployee.salutation || ""} ${selectedEmployee.firstName || ""} ${selectedEmployee.middleName || ""} ${selectedEmployee.lastName || ""}`} />
                <TwoColRow label1="Employee ID" value1={selectedEmployee.employeeID} />
                <TwoColRow label1="Designation" value1={selectedEmployee.designationName} />
                <TwoColRow label1="Date of Joining" value1={selectedEmployee.doj} />
                <TwoColRow label1="Pay Month" value1={`${month} ${year}`} />
              </div>

              <div className="col-span-1 border border-gray-300 rounded px-4 py-2 bg-green-50 text-left">
                <p className="text-2xl font-semibold">₹{inHandSalary}</p>
                <p className="text-lg text-gray-800">Total Payable</p>
                
                <div className="mt-2 text-left space-y-1">
                  <div className="flex justify-between text-lg">
                    <div className="flex-1">Working days: <span className="font-semibold">{totalWorkingDays}</span></div>
                    <div className="flex-1 text-right">Lop: <span className="font-semibold">{LOP}</span></div>
                  </div>
                  <div className="flex justify-between text-lg">
                    <div className="flex-1">Total paid days: <span className="font-semibold">{totalPaidDays}</span></div>
                    <div className="flex-1 text-right">Leaves: <span className="font-semibold">{leaves}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="border border-black p-2">
              <h3 className="text-xl font-semibold mb-3">Earnings</h3>
              <table className="w-full border border-black text-lg">
                <thead>
                  <tr className="bg-gray-200 text-center">
                    <th className="border p-1">SL No</th>
                    <th className="border p-1">Head</th>
                    <th className="border p-1">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {earningDetails.map((e, i) => (
                    <tr key={i}>
                      <td className="border p-2 text-center">{i + 1}</td>
                      <td className="border p-2 font-semibold text-left">{e.headName}</td>
                      <td className="border p-2 text-center font-semibold">₹{Number(e.value)}</td>
                    </tr>
                  ))}
                  <tr className="bg-green-50 font-bold border-t border-green-200 text-black">
                    <td className="border p-2 text-center">
                      {earningDetails.length + 1}
                    </td>
                    <td className="border p-2 text-left">
                      OT ({otHours || 0} HRS)
                    </td>
                    <td className="border p-2 text-center">
                      ₹{(otAmount || 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="border border-black p-2">
              <h3 className="text-xl font-semibold mb-3">Deductions</h3>
              <table className="w-full border border-black text-lg">
                <thead>
                  <tr className="bg-gray-200 text-center">
                    <th className="border p-1">Sl No</th>
                    <th className="border p-1">Head</th>
                    <th className="border p-1">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {deductionDetails.map((d, i) => (
                    <tr key={i}>
                      <td className="border p-2 text-center">{i + 1}</td>
                      <td className="border p-2 font-semibold uppercase text-left">{d.headName}</td>
                      <td className="border p-2 text-center font-semibold">₹{Number(d.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between items-end mt-4">
            <div className="border-2 border-gray-400 rounded-lg px-4 py-2 w-96 bg-white shadow-sm">
              <div className="flex justify-between mb-2 text-xl">
                <span className="text-gray-950 font-bold">Gross Salary</span>
                <span className="font-bold">₹{grossSalary}</span>
              </div>
              <div className="flex justify-between my-2 font-bold text-blue-800 border-t-2 border-blue-200 pt-2 text-2xl">
                <span>Net Salary</span>
                <span>₹{netSalary}</span>
              </div>
            </div>

            <div className="text-right pb-1 pr-24">
              <p className="text-sm font-semibold italic text-gray-700 border-t border-gray-400 pt-1">
                This is a computer-generated payslip and does not require a physical signature.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaySlipGenerateEmployeeList;